import { z } from 'zod';
import { AccountCode, CcyCodeSchema, IsoDate, MoneyStr } from './common.js';

// ─── Аудит балансов: сверка факта (chat-balances) с планом (проводки) ────────
//
// Знаковая модель единая: плюс = «мы должны», минус = «нам должны».
// delta = external − internal; |delta| > ε → расхождение.
// Подробности — docs/superpowers/specs/2026-07-02-balance-audit-design.md.

/**
 * Допуск сверки: единый для reconcile (расчёт mismatch), ack-покрытия
 * (совпадение acked_delta с текущей дельтой) и UI-подсветки кандидатов
 * в журнале движений бота. Полкопейки при MONEY_SCALE=4 — поглощает
 * округление сумм внешнего API.
 */
export const AUDIT_EPSILON = '0.005';

export const AuditStatusSchema = z.enum([
  'ok', // все валюты сходятся
  'mismatch', // есть непокрытая ack'ом дельта — искать операцию в чате
  'acked', // все дельты покрыты действующими ack'ами
  'unmapped', // чат есть в API бота, контрагент не найден по telegram_chat_id
  'no-external', // в системе есть план по чату, но бот его не отдал
]);
export type AuditStatus = z.infer<typeof AuditStatusSchema>;

/** Действующий ack, покрывающий текущую дельту (chat, ccy). */
export const AuditAckDto = z.object({
  id: z.number().int(),
  comment: z.string(),
  createdBy: z.string().nullable(), // display name пользователя
  createdAt: z.string(), // ISO timestamptz
});
export type AuditAckDto = z.infer<typeof AuditAckDto>;

export const AuditCcyRow = z.object({
  ccy: CcyCodeSchema,
  external: MoneyStr, // факт (бот)
  internal: MoneyStr, // план (initial + pending)
  delta: MoneyStr, // external − internal
  mismatch: z.boolean(), // |delta| > ε (независимо от ack)
  ack: AuditAckDto.nullable(), // действующий ack, покрывающий ЭТУ дельту
  // Окно расхождения из снапшотов: «между divergedAfter и divergedBefore».
  // null-значения: нет снапшот-истории (или дельта старше первого снапшота).
  divergedAfter: z.string().nullable(),
  divergedBefore: z.string().nullable(),
});
export type AuditCcyRow = z.infer<typeof AuditCcyRow>;

export const AuditChatRow = z.object({
  chatId: z.string(), // "-100…" bigint как строка (JSON-safe)
  title: z.string(),
  accountCode: AccountCode.nullable(),
  accountKind: z.enum(['client', 'executor']).nullable(),
  inverted: z.boolean(),
  status: AuditStatusSchema,
  rows: z.array(AuditCcyRow),
});
export type AuditChatRow = z.infer<typeof AuditChatRow>;

export const AuditKpi = z.object({
  total: z.number().int(),
  ok: z.number().int(),
  mismatch: z.number().int(),
  acked: z.number().int(),
  unmapped: z.number().int(),
  noExternal: z.number().int(),
});
export type AuditKpi = z.infer<typeof AuditKpi>;

/** Ответ GET /balance-audit. Состояния как у ChatBalancesResponse. */
export const BalanceAuditResponse = z.object({
  configured: z.boolean(),
  asOf: IsoDate.nullable(),
  lastRunAt: z.string().nullable(), // taken_at последнего снапшота (любого статуса)
  kpi: AuditKpi,
  chats: z.array(AuditChatRow),
  error: z.string().nullable(),
});
export type BalanceAuditResponse = z.infer<typeof BalanceAuditResponse>;

export const AuditHistoryPoint = z.object({
  takenAt: z.string(),
  delta: MoneyStr, // 0 если в прогоне дельты не было
});
export type AuditHistoryPoint = z.infer<typeof AuditHistoryPoint>;

export const AuditHistoryResponse = z.object({
  chatId: z.string(),
  ccy: CcyCodeSchema,
  points: z.array(AuditHistoryPoint),
});
export type AuditHistoryResponse = z.infer<typeof AuditHistoryResponse>;

/**
 * Пометить дельту объяснённой. `delta` — значение с текущей сверки на клиенте:
 * сервер сравнивает с актуальной дельтой и отвечает 409, если она уже изменилась
 * (защита от гонки «объяснил не то, что видел»).
 */
export const CreateAckInput = z.object({
  chatId: z.string().min(1),
  ccy: CcyCodeSchema,
  delta: MoneyStr,
  comment: z.string().min(1).max(500),
});
export type CreateAckInput = z.infer<typeof CreateAckInput>;

// ─── Журнал движений бота (пооперационный аудит) ────────────────────────────
//
// Внешний API: GET <transactionsApiUrl>?chat_id&date&limit&offset (Bearer).
// date_time — локальное время бота, ISO-8601 без TZ. amount — знаковая строка
// (пополнение +, списание −). asset — нижний регистр. Схема нестрогая:
// валидируем только используемое.

export const ExternalBotTxn = z.object({
  date_time: z.string(),
  chat: z.object({ id: z.number().int() }).passthrough(),
  user: z.object({ name: z.string().nullish() }).passthrough().nullish(),
  amount: z.string(),
  asset: z.string(),
});
export type ExternalBotTxn = z.infer<typeof ExternalBotTxn>;

export const ExternalBotTxnsResponse = z.object({
  pagination: z.object({
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
    count: z.number().int(),
  }),
  transactions: z.array(ExternalBotTxn),
});
export type ExternalBotTxnsResponse = z.infer<typeof ExternalBotTxnsResponse>;

/** Одно движение баланса в боте (после нормализации и инверсии знака). */
export const BotTxn = z.object({
  dateTime: z.string(), // как отдал апстрим (локальное время бота, без TZ)
  userName: z.string().nullable(),
  ccy: z.string(), // upper; может не входить в CCY_CODES — журнал показываем как есть
  amount: MoneyStr, // знак уже инвертирован для invert_balance-чатов
});
export type BotTxn = z.infer<typeof BotTxn>;

export const BotTxnsResponse = z.object({
  configured: z.boolean(), // enabled + transactionsApiUrl + токен заданы
  total: z.number().int(),
  items: z.array(BotTxn),
  error: z.string().nullable(),
});
export type BotTxnsResponse = z.infer<typeof BotTxnsResponse>;
